import { useEffect, useState } from "react";
import {
  Languages, ArrowRight, Sparkles, Mail, Lock, User as UserIcon,
  Upload, Play, X, Quote, Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import LegalFooter from "../components/LegalFooter";
import FeatureSlideshow from "../components/FeatureSlideshow";
import Seo from "../components/Seo";
import { FreeTrialText } from "../components/PricingCopy";
import ForgotPasswordModal from "../components/ForgotPasswordModal";

// Pre-computed sample — one line of Bollywood-style dialogue with a
// literal machine translation vs. LinguaSub's personality-preserving one.
const SAMPLE_LINES = [
  {
    src: "अरे यार, तूने मेरा दिल ही तोड़ दिया, बहन।",
    literal: "Oh friend, you broke my heart, sister.",
    ours: "Damn, you really shattered me, sis.",
  },
  {
    src: "भाई, ये कोई फ़िल्मी डायलॉग नहीं है — असली बात कर।",
    literal: "Brother, this is not a film dialogue — talk real thing.",
    ours: "Bro, cut the Bollywood act — talk to me straight.",
  },
  {
    src: "पगला गया है क्या तू? इतनी रात को शादी की बात!",
    literal: "Have you become mad? At this night marriage talk!",
    ours: "You've lost the plot — bringing up marriage at this hour?",
  },
  {
    src: "क्यों बे, अंग्रेज़ी बहुत आती है तुझे?",
    literal: "Why bay, English much comes to you?",
    ours: "Oh, look at you — Mr. Fancy English, huh?",
  },
];

const MISSED_BY_GENERIC = [
  { tag: "Hinglish", ex: "\"Bhai I'm literally dying yaar\"" },
  { tag: "Regional slang", ex: "\"Ekdum jhakaas, boss!\"" },
  { tag: "Honorifics", ex: "bhaiya · didi · beta · janab" },
  { tag: "Relationships", ex: "\"Chachi ki bhatiji ke devar ka beta\"" },
  { tag: "Class-coded language", ex: "\"Aap\" vs \"tu\" vs \"tum\"" },
  { tag: "Bollywood expressions", ex: "\"Picture abhi baaki hai\"" },
  { tag: "Jokes & wordplay", ex: "\"Sasural mein sasu ka sass\"" },
  { tag: "Double meanings", ex: "\"Uska character loose hai\"" },
  { tag: "Cultural references", ex: "\"Karva Chauth ka chand\"" },
  { tag: "Abusive language", ex: "toned & softened, not censored" },
  { tag: "Sarcasm", ex: "\"Wah, kya baat hai!\" (said flat)" },
];

function formatDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sampleOpen, setSampleOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const scrollToAuth = () => {
    // Users who explicitly click "Sign in" in the nav clearly want the
    // sign-in tab, not the default sign-up one.
    setMode("signin");
    setError("");
    const el = document.getElementById("auth-section");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Capture the `?ref=xxxxxxxx&ch=<channel>` query params on first landing
  // so we can attach them to the eventual signup call, even if the visitor
  // bounces around the site first. Stored under a namespaced localStorage
  // key (short 30-day TTL enforced by the object shape).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || "").trim();
    if (!ref || !/^[A-Za-z0-9_-]{4,32}$/.test(ref)) return;
    // Whitelist known channels so a mistyped or malicious ?ch= can't
    // pollute analytics. Everything else falls through as null → "direct"
    // once the user signs up.
    const KNOWN = new Set(["whatsapp", "twitter", "copy", "link", "direct"]);
    const rawCh = (params.get("ch") || "").trim().toLowerCase();
    const channel = KNOWN.has(rawCh) ? rawCh : null;
    try {
      localStorage.setItem(
        "linguasub_ref",
        JSON.stringify({ code: ref, channel, at: Date.now() }),
      );
    } catch { /* private mode / storage disabled — silent */ }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "signup" ? "/auth/register" : "/auth/login";
      // On signup, look up the captured referral code and attach it. Codes
      // older than 30 days are ignored to avoid awarding rewards on stale
      // links that were never really used.
      let referralCode = null;
      let referralChannel = null;
      if (mode === "signup") {
        try {
          const raw = localStorage.getItem("linguasub_ref");
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed?.code && Date.now() - (parsed.at || 0) < 30 * 86400 * 1000) {
              referralCode = parsed.code;
              referralChannel = parsed.channel || null;
            }
          }
        } catch { /* noop */ }
      }
      const body =
        mode === "signup"
          ? {
              email, password, name,
              ...(referralCode ? { referral_code: referralCode } : {}),
              ...(referralChannel ? { referral_channel: referralChannel } : {}),
            }
          : { email, password };
      const { data } = await api.post(endpoint, body);
      // Clear the captured code once we've used it so a shared browser
      // doesn't credit the same referrer for every subsequent sign-up.
      if (mode === "signup" && referralCode) {
        try { localStorage.removeItem("linguasub_ref"); } catch { /* noop */ }
      }
      // On signup only: set a one-shot flag so the Dashboard shows the
      // welcome-modal with the new user's referral link. Keyed by user_id
      // so multiple accounts sharing a device each see it exactly once.
      if (mode === "signup" && data?.user_id) {
        try { localStorage.setItem("linguasub_show_welcome", data.user_id); } catch { /* noop */ }
      }
      setUser(data);
      toast.success(mode === "signup" ? "Welcome to LinguaSub" : "Signed in");
      navigate("/dashboard", { replace: true, state: { user: data } });
    } catch (err) {
      const msg = formatDetail(err.response?.data?.detail) || err.message || "Failed";
      // UX rescue: a returning user often lands on this page, ignores the
      // tab strip (default is "Sign up"), types their existing email +
      // password, and clicks the primary button — which submits /register
      // and 400s with "Email already registered". Auto-retry as login with
      // the same credentials so their intent still succeeds.
      if (
        mode === "signup"
        && typeof msg === "string"
        && msg.toLowerCase().includes("already registered")
      ) {
        try {
          const { data } = await api.post("/auth/login", { email, password });
          setUser(data);
          toast.success("Welcome back — you already had an account, so we signed you in.");
          navigate("/dashboard", { replace: true, state: { user: data } });
          return;
        } catch (loginErr) {
          // Password wrong — flip to sign-in mode with a helpful hint.
          setMode("signin");
          const detail = formatDetail(loginErr.response?.data?.detail) || "";
          if (loginErr.response?.status === 429) {
            setError(detail || "Too many failed attempts. Try again in 15 minutes or use Google sign-in.");
          } else {
            setError("This email is already registered. Enter your existing password to sign in, or use “Continue with Google”.");
          }
          return;
        }
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <Seo path="/" />
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-zinc-800 bg-zinc-950/85 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Languages className="w-5 h-5 text-blue-500" />
            <span className="font-heading text-lg font-black tracking-tighter">LinguaSub</span>
          </div>
          <nav className="flex items-center gap-3">
            <Link to="/pricing" data-testid="landing-pricing-link" className="hidden sm:inline text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors">Pricing</Link>
            <button
              onClick={scrollToAuth}
              data-testid="landing-signin-link"
              className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
            >
              Sign in
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHx2aWRlbyUyMGVkaXRpbmclMjBzdHVkaW8lMjBkYXJrfGVufDB8fHx8MTc4NzExMjMyOXww&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/70 via-zinc-950/85 to-zinc-950" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-16 pb-20 md:pt-28 md:pb-32">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-blue-400 border border-blue-500/20 bg-blue-500/5 px-3 py-1.5 rounded-full mb-8">
            <Sparkles className="w-3 h-3" />
            <span>21 languages · Whisper + GPT-5.4</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter leading-[0.95] mb-6 max-w-4xl">
            AI subtitles that preserve <span className="text-blue-500">slang, humour</span>, emotion, character personality and cultural meaning.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-base md:text-lg leading-relaxed mb-10">
            Generic tools translate words. LinguaSub translates <em className="not-italic text-white">how a person actually talks</em> — the tone, the sarcasm, the punchline, the pain. Built for Indian dialogue, works across 21 languages either direction.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              data-testid="cta-upload-video"
              onClick={scrollToAuth}
              className="h-12 px-6 bg-white text-zinc-950 hover:bg-zinc-200 rounded-none font-semibold text-sm inline-flex items-center gap-2 transition-colors duration-200"
            >
              <Upload className="w-4 h-4" /> Upload a video <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              data-testid="cta-try-sample"
              onClick={() => setSampleOpen(true)}
              className="h-12 px-6 border border-zinc-700 bg-transparent hover:bg-zinc-900 text-white rounded-none font-semibold text-sm inline-flex items-center gap-2 transition-colors duration-200"
            >
              <Play className="w-4 h-4" /> Try a sample <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* What generic translation misses */}
      <section className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
            The gap
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter leading-tight mb-10 max-w-3xl">
            Indian dialogue contains exactly the things generic translation struggles with.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MISSED_BY_GENERIC.map((row) => (
              <div key={row.tag} data-testid={`gap-item-${row.tag.replace(/\W+/g, "-").toLowerCase()}`} className="border border-zinc-800 bg-zinc-900/40 p-5 hover:border-blue-500/40 transition-colors duration-200">
                <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-2">
                  {row.tag}
                </div>
                <div className="text-sm text-zinc-300 leading-relaxed">{row.ex}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before / after mini-showcase */}
      <section className="border-b border-zinc-800 bg-zinc-950">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
            Side-by-side
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter leading-tight mb-10 max-w-3xl">
            The same Hindi line. One machine, one <span className="text-blue-500">human-sounding</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ShowcaseColumn
              label="Original (Hindi)"
              tone="text-zinc-400"
              items={SAMPLE_LINES.map((s) => s.src)}
              testId="showcase-src"
            />
            <ShowcaseColumn
              label="Generic tool"
              tone="text-zinc-500 line-through decoration-red-400/40"
              items={SAMPLE_LINES.map((s) => s.literal)}
              testId="showcase-literal"
            />
            <ShowcaseColumn
              label="LinguaSub"
              tone="text-white"
              badge
              items={SAMPLE_LINES.map((s) => s.ours)}
              testId="showcase-ours"
            />
          </div>
          <div className="mt-8 text-xs font-mono text-zinc-600">
            Every translation carries tone. When you upload your own clip, LinguaSub reads the full transcript first — so a sarcastic "Wah, kya baat hai" stays sarcastic in English.
          </div>
        </div>
      </section>

      {/* Homepage engine sample — visible to signed-out visitors */}
      <FeatureSlideshow />

      {/* Auth section */}
      <section id="auth-section" className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3 flex items-center gap-1.5">
                <Zap className="w-3 h-3" /> Get started free
              </div>
              <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter leading-tight mb-6">
                <FreeTrialText />. No card. No monthly renewal.
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-8">
                Sign up, upload a clip up to 5 GB, pick from any of 21 languages either direction, download SRT + VTT. Ready when you are.
              </p>
              <ul className="space-y-3 text-sm text-zinc-300">
                <FeatureLine text="Whisper transcription with segment timestamps" />
                <FeatureLine text="Upload videos up to 5 GB" />
                <FeatureLine text="Edit subtitles with timeline control" />
                <FeatureLine text="Export in SRT and VTT format for YouTube, Instagram, TikTok" />
                <FeatureLine text="Indian language support including Hindi and regional languages" />
                <FeatureLine text="Media auto-deleted after processing" />
              </ul>
            </div>

            <div className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8">
              <Tabs value={mode} onValueChange={(v) => { setMode(v); setError(""); }} className="w-full mb-5">
                <TabsList className="w-full bg-zinc-900 border border-zinc-800 rounded-none h-auto p-0 grid grid-cols-2">
                  <TabsTrigger value="signup" data-testid="auth-tab-signup" className="rounded-none border-r border-zinc-800 py-3 data-[state=active]:bg-white data-[state=active]:text-zinc-950 text-zinc-400 hover:text-white font-mono uppercase tracking-widest text-xs transition-colors">
                    Sign up
                  </TabsTrigger>
                  <TabsTrigger value="signin" data-testid="auth-tab-signin" className="rounded-none py-3 data-[state=active]:bg-white data-[state=active]:text-zinc-950 text-zinc-400 hover:text-white font-mono uppercase tracking-widest text-xs transition-colors">
                    Sign in
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="signup" className="mt-6 m-0">
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
                    <FieldInput icon={<UserIcon className="w-4 h-4" />} type="text" placeholder="Your name" value={name} onChange={(v) => setName(v)} testId="signup-name" required />
                    <FieldInput icon={<Mail className="w-4 h-4" />} type="email" placeholder="you@company.com" value={email} onChange={(v) => setEmail(v)} testId="signup-email" required />
                    <FieldInput icon={<Lock className="w-4 h-4" />} type="password" placeholder="At least 8 characters" value={password} onChange={(v) => setPassword(v)} testId="signup-password" minLength={8} required />
                    {error && <ErrorMsg text={error} />}
                    <SubmitBtn loading={loading} testId="signup-submit" label="Create account" />
                  </form>
                </TabsContent>

                <TabsContent value="signin" className="mt-6 m-0">
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="signin-form">
                    <FieldInput icon={<Mail className="w-4 h-4" />} type="email" placeholder="you@company.com" value={email} onChange={(v) => setEmail(v)} testId="signin-email" required />
                    <FieldInput icon={<Lock className="w-4 h-4" />} type="password" placeholder="Password" value={password} onChange={(v) => setPassword(v)} testId="signin-password" required />
                    {error && <ErrorMsg text={error} />}
                    <SubmitBtn loading={loading} testId="signin-submit" label="Sign in" />
                    <div className="flex justify-end pt-1">
                      <button
                        type="button"
                        data-testid="forgot-password-link"
                        onClick={() => setForgotOpen(true)}
                        className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                  </form>
                </TabsContent>
              </Tabs>

              <div className="relative my-6 flex items-center gap-4">
                <div className="h-px flex-1 bg-zinc-800" />
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">or</span>
                <div className="h-px flex-1 bg-zinc-800" />
              </div>

              <Button
                data-testid="google-auth-btn"
                onClick={handleGoogleLogin}
                className="w-full h-11 bg-transparent border border-zinc-800 text-white hover:bg-zinc-900 hover:text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center gap-3"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="text-sm">Continue with Google</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <LegalFooter />
      </div>

      {/* Forgot password modal */}
      <ForgotPasswordModal
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        initialEmail={email}
      />

      {/* Sample modal */}
      <Dialog open={sampleOpen} onOpenChange={setSampleOpen}>
        <DialogContent
          data-testid="sample-modal"
          className="bg-zinc-950 border border-zinc-800 rounded-none max-w-3xl w-[92vw] p-0 text-white max-h-[90dvh] overflow-hidden [&>button:last-child]:hidden"
        >
          <div className="p-6 md:p-8 border-b border-zinc-800 flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-2">Live sample · Hindi → English</div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">See it, feel the difference.</h3>
            </div>
            <button onClick={() => setSampleOpen(false)} data-testid="sample-close" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-6 md:p-8 space-y-6 overflow-y-auto">
            {SAMPLE_LINES.map((s, i) => (
              <div key={s.src} className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-4 pb-6 border-b border-zinc-800 last:border-b-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">#{String(i + 1).padStart(2, "0")}</div>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Quote className="w-3 h-3 text-zinc-600 mt-1 flex-shrink-0" />
                    <div className="text-sm text-zinc-400 font-mono">{s.src}</div>
                  </div>
                  <div className="text-xs text-zinc-500 line-through decoration-red-400/40">
                    Generic: {s.literal}
                  </div>
                  <div className="text-base text-white leading-relaxed">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mr-2">LinguaSub</span>
                    {s.ours}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="p-6 md:p-8 border-t border-zinc-800 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => { setSampleOpen(false); setTimeout(scrollToAuth, 200); }}
              data-testid="sample-cta-signup"
              className="h-11 px-5 bg-white text-zinc-950 hover:bg-zinc-200 rounded-none font-semibold inline-flex items-center gap-2 transition-colors duration-200"
            >
              <Upload className="w-4 h-4" /> Upload your own <ArrowRight className="w-4 h-4" />
            </Button>
            <Link to="/pricing" data-testid="sample-pricing-link" className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">See pricing →</Link>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ShowcaseColumn({ label, items, tone, badge, testId }) {
  return (
    <div data-testid={testId} className={`border ${badge ? "border-blue-500/40 bg-blue-500/5" : "border-zinc-800 bg-zinc-900/40"} p-5 md:p-6`}>
      <div className={`text-[10px] font-mono uppercase tracking-widest mb-4 ${badge ? "text-blue-400" : "text-zinc-500"}`}>{label}</div>
      <ul className="space-y-3">
        {items.map((t) => (
          <li key={t} className={`text-sm leading-relaxed ${tone}`}>{t}</li>
        ))}
      </ul>
    </div>
  );
}

function FeatureLine({ text }) {
  return (
    <li className="flex items-start gap-3">
      <span className="inline-block w-1 h-1 rounded-full bg-emerald-400 mt-2 flex-shrink-0" />
      <span>{text}</span>
    </li>
  );
}

function FieldInput({ icon, testId, onChange, ...rest }) {
  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none">{icon}</span>
      <Input
        {...rest}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="pl-10 h-11 bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
      />
    </div>
  );
}

function ErrorMsg({ text }) {
  return (
    <div data-testid="auth-error" className="text-xs font-mono text-red-400 border border-red-500/20 bg-red-500/5 px-3 py-2">
      {text}
    </div>
  );
}

function SubmitBtn({ loading, testId, label }) {
  return (
    <Button
      type="submit"
      disabled={loading}
      data-testid={testId}
      className="w-full h-11 bg-white text-zinc-950 hover:bg-zinc-200 disabled:bg-zinc-800 disabled:text-zinc-500 font-semibold rounded-md transition-colors duration-200"
    >
      {loading ? "Please wait..." : label}
    </Button>
  );
}
